import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { STUDIO_PROJECTS_COLLECTION } from "../constants";

function nowIso() {
  return new Date().toISOString();
}

export function emptyProjectForm() {
  return {
    name: "",
    company: "",
    description: "",
    environment: "development",
    firebaseProjectId: "",
    region: "",
    tags: [],
    notes: "",
    favorite: false,
    archived: false,
  };
}

export function normalizeProject(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    name: data.name || "",
    company: data.company || "",
    description: data.description || "",
    environment: data.environment || "development",
    firebaseProjectId: data.firebaseProjectId || "",
    region: data.region || "",
    tags: Array.isArray(data.tags) ? data.tags : [],
    notes: data.notes || "",
    favorite: Boolean(data.favorite),
    archived: Boolean(data.archived),
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
    createdBy: data.createdBy || "",
  };
}

export async function listStudioProjects() {
  const snap = await getDocs(collection(db, STUDIO_PROJECTS_COLLECTION));
  return snap.docs.map(normalizeProject).sort((a, b) => {
    const ta = a.updatedAt || a.createdAt || "";
    const tb = b.updatedAt || b.createdAt || "";
    return tb.localeCompare(ta);
  });
}

export async function getStudioProject(id) {
  const snap = await getDoc(doc(db, STUDIO_PROJECTS_COLLECTION, id));
  if (!snap.exists()) return null;
  return normalizeProject(snap);
}

export async function isFirebaseProjectIdTaken(firebaseProjectId, excludeId = null) {
  const id = String(firebaseProjectId || "").trim();
  if (!id) return false;
  const snap = await getDocs(
    query(collection(db, STUDIO_PROJECTS_COLLECTION), where("firebaseProjectId", "==", id))
  );
  return snap.docs.some((d) => d.id !== excludeId);
}

export async function createStudioProject(payload, createdBy) {
  const name = String(payload.name || "").trim();
  const firebaseProjectId = String(payload.firebaseProjectId || "").trim();
  if (!name) throw new Error("Project name is required");
  if (!firebaseProjectId) throw new Error("Firebase Project ID is required");
  if (await isFirebaseProjectIdTaken(firebaseProjectId)) {
    throw new Error("A project with this Firebase Project ID already exists");
  }

  const ts = nowIso();
  const ref = await addDoc(collection(db, STUDIO_PROJECTS_COLLECTION), {
    name,
    company: String(payload.company || "").trim(),
    description: String(payload.description || "").trim(),
    environment: payload.environment || "development",
    firebaseProjectId,
    region: String(payload.region || "").trim(),
    tags: Array.isArray(payload.tags) ? payload.tags.filter(Boolean) : [],
    notes: String(payload.notes || "").trim(),
    favorite: Boolean(payload.favorite),
    archived: false,
    createdAt: ts,
    updatedAt: ts,
    createdBy: createdBy || "",
  });
  return getStudioProject(ref.id);
}

export async function updateStudioProject(id, payload, updatedBy) {
  const name = String(payload.name || "").trim();
  const firebaseProjectId = String(payload.firebaseProjectId || "").trim();
  if (!name) throw new Error("Project name is required");
  if (!firebaseProjectId) throw new Error("Firebase Project ID is required");
  if (await isFirebaseProjectIdTaken(firebaseProjectId, id)) {
    throw new Error("A project with this Firebase Project ID already exists");
  }

  const patch = {
    name,
    company: String(payload.company || "").trim(),
    description: String(payload.description || "").trim(),
    environment: payload.environment || "development",
    firebaseProjectId,
    region: String(payload.region || "").trim(),
    tags: Array.isArray(payload.tags) ? payload.tags.filter(Boolean) : [],
    notes: String(payload.notes || "").trim(),
    favorite: Boolean(payload.favorite),
    archived: Boolean(payload.archived),
    updatedAt: nowIso(),
    updatedBy: updatedBy || "",
  };
  await updateDoc(doc(db, STUDIO_PROJECTS_COLLECTION, id), patch);
  return getStudioProject(id);
}

export async function setProjectArchived(id, archived, actor) {
  await updateDoc(doc(db, STUDIO_PROJECTS_COLLECTION, id), {
    archived: Boolean(archived),
    updatedAt: nowIso(),
    updatedBy: actor || "",
  });
  return getStudioProject(id);
}

export async function setProjectFavorite(id, favorite, actor) {
  await updateDoc(doc(db, STUDIO_PROJECTS_COLLECTION, id), {
    favorite: Boolean(favorite),
    updatedAt: nowIso(),
    updatedBy: actor || "",
  });
  return getStudioProject(id);
}

export async function deleteStudioProject(id) {
  await deleteDoc(doc(db, STUDIO_PROJECTS_COLLECTION, id));
}

export async function duplicateStudioProject(sourceId, createdBy) {
  const source = await getStudioProject(sourceId);
  if (!source) throw new Error("Source project not found");

  let firebaseProjectId = source.firebaseProjectId;
  let suffix = 1;
  while (await isFirebaseProjectIdTaken(firebaseProjectId)) {
    firebaseProjectId = `${source.firebaseProjectId}-copy${suffix > 1 ? suffix : ""}`;
    suffix += 1;
  }

  return createStudioProject(
    {
      ...source,
      name: `${source.name} (Copy)`,
      firebaseProjectId,
      favorite: false,
      archived: false,
    },
    createdBy
  );
}

export function computeProjectStats(projects) {
  const active = projects.filter((p) => !p.archived);
  return {
    total: projects.length,
    production: active.filter((p) => p.environment === "production").length,
    testing: active.filter((p) => p.environment === "testing").length,
    development: active.filter((p) => p.environment === "development").length,
    archived: projects.filter((p) => p.archived).length,
    favorites: active.filter((p) => p.favorite).length,
  };
}
